/**
 * Deploy-channel namespacing for browser storage keys.
 *
 * In 'stable' channel, keys are unchanged.
 * In preview channels (e.g. 'canary'), keys are prefixed: `canary:app_theme`.
 * This prevents settings from bleeding across deploy channels.
 *
 * These are the lowest layer of the storage stack — both `storage.ts` (which re-exports
 * them, so the ~13 existing `@/lib/infra/storage` imports keep working) and `gateStorage.ts`
 * build on them. They live in their own module so that gate persistence can depend on key
 * namespacing without the two modules importing each other in a cycle.
 */

/** Current deploy channel (injected at build time). */
export function getChannel(): string {
  try {
    return __CHANNEL__;
  } catch {
    return 'stable';
  }
}

/** True when running a non-stable channel (canary, beta, etc.). */
export function isPreviewChannel(): boolean {
  return getChannel() !== 'stable';
}

/**
 * Return a channel-namespaced storage key.
 * Stable channel returns the key as-is for backwards compatibility.
 */
export function storageKey(key: string): string {
  const ch = getChannel();
  return ch === 'stable' ? key : `${ch}:${key}`;
}
